<template>
  <v-container class="py-12 px-5 px-md-7 pa-md-20">
    <v-card-title
      class="display-1 font-weight-bold pa-0 mb-8 letter-spacing-normal"
    >
      {{ getTitle() }}
    </v-card-title>
    <v-row class="justify-lg-space-between align-center">
      <v-col class="pa-0">
        <v-card-title class="py-0 title font-weight-bold letter-spacing-normal">
          Most Frequent
        </v-card-title>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col class="py-3 mb-5">
        <v-card class="px-3 py-3">
          <v-skeleton-loader
            v-if="!isEditMode && !speciesModel.fetched"
            type="list-item-two-line"
          />
          <v-list
            v-else
            max-height="400"
            class="overflow-auto species-list"
            ref="listElement"
          >
            <v-list-item v-if="!attendeeSpecies.length" class="d-flex">
              Nothing to show here!
            </v-list-item>
            <v-list-item
              class="d-flex"
              v-for="species in attendeeSpecies"
              :key="species.id"
            >
              <v-list-item-avatar size="80" class="ma-0" tile>
                <v-img
                  :src="getMedia(species)"
                  lazy-src="/placeholder.jpg"
                  aspect-ratio="1"
                  class="grey lighten-2"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title
                  class="ml-5 title font-weight-bold letter-spacing-normal"
                >
                  {{ species.name }}
                  <v-spacer />
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="notranslate">
                {{ request.getCount(member.id, species.id) }}
              </v-list-item-action>
              <div class="d-flex elevation-3 ml-2">
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="reduceSighting(species)"
                >
                  <v-icon
                    title="Reduce Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $minus
                  </v-icon>
                </v-list-item-action>
                <v-divider :vertical="true" />
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="increaseSighting(species)"
                >
                  <v-icon
                    title="Increase Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $plus
                  </v-icon>
                </v-list-item-action>
              </div>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="autocomplete-container">
      <v-col class="py-3 mb-5">
        <div id="speciesSearch" class="position-relative notranslate">
          <v-autocomplete
            ref="autocomplete"
            v-model="fakeModel"
            :search-input.sync="speciesFilter"
            @input="selectSpecies"
            :items="speciesList"
            :multiple="false"
            :filter="() => true"
            attach="#speciesSearch"
            item-text="name"
            return-object
            label="Search"
            append-icon="$search"
            solo
            class="font-weight-bold"
            color="black"
            @focus="scrollAutocompleteToTop"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SpeciesModel from '@models/Species';
import GroupSightingsRequest from '@request/GroupSightingsRequest';

export default {
  name: 'MemberSightings',
  props: {
    member: {
      type: Object,
      required: true,
    },
    request: {
      type: GroupSightingsRequest,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      speciesModel: new SpeciesModel({
        itemsPerPage: 5,
      }),
      fakeModel: {},
      speciesFilter: '',
      frequentSpecies: [],
      speciesList: [],
      species: [],
      disableSpeciesFilter: false,
    };
  },
  computed: {
    attendeeSpecies() {
      const frequentIds = new Set(this.frequentSpecies.map((s) => s.id));
      const uniqueSpecies = this.species.filter((s) => !frequentIds.has(s.id));
      return [...this.frequentSpecies, ...uniqueSpecies];
    },
  },
  watch: {
    'member.id': {
      handler() {
        this.species = [];
        if (this.speciesFilter) {
          this.speciesFilter = '';
        }
        this.syncSpeciesWithRequest();
      },
      deep: true,
    },
    speciesFilter() {
      if (this.disableSpeciesFilter) {
        this.disableSpeciesFilter = false;
        return;
      }
      if (!this.speciesModel.fetched) {
        return;
      }
      this.fetchSpecies();
    },
  },
  methods: {
    getTitle() {
      let name = this.member.first_name;
      if (name.endsWith('s')) {
        return `${name}' Sightings`;
      }
      return `${name}'s Sightings`;
    },
    scrollAutocompleteToTop() {
      this.$nextTick(() => {
        const el = this.$refs.autocomplete?.$el;
        if (!el) return;
        el.closest('.autocomplete-container').scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
    async fetchSpecies() {
      const self = this;
      this.speciesModel.setFilters({
        name: this.speciesFilter,
        excludes: [...self.frequentSpecies, ...self.species]
          .map((species) => species.id)
          .join(','),
      });
      let response = await this.speciesModel.get();
      if (response) {
        this.speciesList = response.data;
      }
    },
    async selectSpecies(species) {
      if (!species) {
        return;
      }
      this.disableSpeciesFilter = true;
      this.fakeModel = {};
      await this.$nextTick();
      if (this.attendeeSpecies.find((s) => s.id === species.id)) {
        $showInfo('The species has already been added!');
        return;
      }
      this.species.push(species);
      await this.$nextTick();

      const listElement = this.$refs.listElement;
      listElement?.$el?.lastElementChild?.scrollIntoView();
      await this.fetchSpecies();
    },
    increaseSighting(species) {
      this.request.changeSightingsData(this.member, species);
    },
    reduceSighting(species) {
      this.request.changeSightingsData(this.member, species, -1);
    },
    async getFrequentSpecies() {
      if (this.isEditMode) {
        return [];
      }
      let model = new SpeciesModel({
        itemsPerPage: 4,
        filters: {
          tour_id: this.request.tour_id,
        },
        sortBy: ['frequent'],
        sortDesc: [true],
      });
      let response = await model.get();
      if (response) {
        this.frequentSpecies = response.data;
      }
      await this.$nextTick();
    },
    async syncSpeciesWithRequest() {
      let self = this;
      let attendees = self.request.attendees.filter(
        (attendee) =>
          attendee.member_id === self.member.id &&
          !self.attendeeSpecies.find(
            (species) => species.id === attendee.species.id
          )
      );
      if (attendees.length) {
        let species = attendees.map((data) => data.species);
        self.species = self.species.concat(species);
      }
      await self.$nextTick();
      this.fetchSpecies();
    },
  },
  mounted() {
    this.getFrequentSpecies();
    this.syncSpeciesWithRequest();
  },
};
</script>
